import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "../../../assets/icons/back.svg";
import SendIcon from "../../../assets/icons/send-quotation-icon.svg";
import CalendarIcon from "../../../assets/icons/book-service-icon.svg";
import { getPreneedBookingAppointmentLeadById } from "../../../api/preneed-section/preneed-booking-appointment.api";
import SendQuotationModal from "./send-quotation-modal";
import BookingPackage from "./book-package";
import "./lead-details.scss";
import { useSelector } from "react-redux";
import LeadDetailsInformationForm from "./lead-details-information-form";
import { CreateOutlined } from "@mui/icons-material";
import LeadAddressDetailsInformationForm from "./lead-address-details-information-form";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../constants/routes";
import { useLocation } from "react-router-dom";
import { useSetLoading } from "../../../common/hooks/appearance";

interface LeadDetailsProps {
  leadId: number;
  onBack: () => void;
}

export interface LeadData {
  id: number;
  name: string;
  email: string;
  phone: string;
  needType: string;
  leadSource: string;
  leadStatus: string;
  address?: {
    street_line_1: string;
    street_line_2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
}

const getInterestedProductLabel = (needType: unknown): string => {
  if (needType === null || needType === undefined) return "";

  const mapSingle = (value: string) => {
    const normalized = value
      .toLowerCase()
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (["at need", "atneed", "immediate need"].includes(normalized)) {
      return "Immediate Need";
    }
    if (["pre need", "preneed", "preneed planning"].includes(normalized)) {
      return "Preneed";
    }
    return value.trim();
  };

  const items = String(needType)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) return "";
  return items.map(mapSingle).join(", ");
};

const LeadDetails: React.FC<LeadDetailsProps> = ({ leadId, onBack }) => {
  const location = useLocation();
  const [breadcrumbLink, setBreadcrumbLink] = useState(() => {
    return (location.state?.breadcrumbLink || []).map((item: any) => {
      if (item.label === "Leads" || item.label === "Leads List") {
        return { ...item, link: routes.PRENEED_LEADS };
      }
      return item;
    });
  });
  const [lead, setLead] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendQuotationOpen, setSendQuotationOpen] = useState(false);
  const [showBookPackage, setShowBookPackage] = useState(false);
  const [addressEdit, setAddressEdit] = useState(false);

  const plannerIdFromRedux = useSelector(
    (state: any) => state?.session?.preNeedDetails?.planner?.plannerId,
  );
  const plannerId = useMemo(() => {
    if (plannerIdFromRedux != null) return plannerIdFromRedux;
    try {
      const raw = localStorage.getItem("preneedDetails");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.planner?.plannerId ?? null;
    } catch {
      return null;
    }
  }, [plannerIdFromRedux]);
  // Callback to update lead name in breadcrumb
  const updateLeadNameInBreadcrumb = (newName: string) => {
    setBreadcrumbLink((prev: any) =>
      prev.map((item: any, idx: number) => {
        // Find the last breadcrumb (lead name is always last)
        if (idx === prev.length - 1) {
          return { ...item, label: newName };
        }
        return item;
      }),
    );
  };
  const fetchLeadDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPreneedBookingAppointmentLeadById(leadId);
      if (response.data?.success) {
        const leadData = response.data?.data?.lead || response.data?.data;
        if (leadData) {
          // Map fields if they come in different formats
          const mappedLead: LeadData = {
            id: leadData.id,
            name: leadData.name || leadData.leadName || "",
            email: leadData.email || "",
            phone: leadData.phone || leadData.phoneNumber || "",
            needType: getInterestedProductLabel(leadData.needType),
            leadSource: leadData.leadSource || "",
            leadStatus: leadData.leadStatus || leadData.status || "",
            address: leadData.address || undefined,
          };
          setLead(mappedLead);
        } else {
          setError("Lead data not found in response");
        }
      } else {
        const errorMsg =
          response.data?.message || "Failed to fetch lead details";
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error("Error fetching lead details:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while fetching lead details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  useEffect(() => {
    if (!lead?.name) return;
    setBreadcrumbLink((prev: any[]) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return [
          {
            label: "Leads",
            link: routes.PRENEED_LEADS,
            disablePointerEvents: true,
          },
          { label: "Leads List", link: "#" },
          {
            label: lead.name,
            link: routes.PRENEED_LEAD_DETAILS.replace(":id", String(leadId)),
            disablePointerEvents: true,
          },
        ];
      }
      const lastIndex = prev.length - 1;
      return prev.map((item: any, idx: number) =>
        idx === lastIndex ? { ...item, label: lead.name } : item,
      );
    });
  }, [lead?.name, leadId]);

  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" p={4}>
  //       <CircularProgress size={40} />
  //     </Box>
  //   );
  // }
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  if (loading && !lead) {
    return "Loading";
  }

  if (error || !lead) {
    return (
      <Box p={4} textAlign="center">
        {/* <Typography color="error">{error || "Lead not found"}</Typography> */}
        <Button onClick={onBack} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  if (showBookPackage) {
    const initialLead = {
      id: lead.id,
      leadName: lead.name,
      email: lead.email,
      phoneNumber: lead.phone,
      address: lead.address
        ? {
          street: [lead.address.street_line_1, lead.address.street_line_2]
            .filter(Boolean)
            .join(", "),
          city: lead.address.city,
          state: lead.address.state,
          zipcode: lead.address.zipcode,
          country: lead.address.country,
        }
        : undefined,
    };
    return (
      <BookingPackage
        leadId={leadId}
        onBack={() => setShowBookPackage(false)}
        initialLead={initialLead}
      />
    );
  }

  const infoItems = [
    { label: "Full Name", value: lead.name },
    { label: "Email Address", value: lead.email },
    { label: "Phone Number", value: lead.phone },
    { label: "Interested Product", value: lead.needType },
    { label: "Lead Source", value: lead.leadSource },
    { label: "Status", value: lead.leadStatus },
  ];

  const addressItems = [
    { label: "Street Line 1", value: lead.address?.street_line_1 },
    { label: "Street Line 2", value: lead.address?.street_line_2 },
    { label: "City", value: lead.address?.city },
    { label: "State", value: lead.address?.state },
    { label: "Country", value: lead.address?.country },
    { label: "Zipcode", value: lead.address?.zipcode },
  ];

  return (
    <div className="lead-details-main-wrapper">
      <div
        style={{
          flexDirection: "column",
          height: "100%",
        }}
      >
        <ProfileHeader breadcrumbLink={breadcrumbLink} mode="preNeed" />

        <Box className="lead-details">
          {/* <Box className="lead-details__back" onClick={onBack}>
        <img
          src={ArrowBackIcon}
          alt="back"
          className="lead-details__back-icon"
        />
        <Typography variant="body1" className="lead-details__back-text">
          Back to Leads
        </Typography>
      </Box> */}
          <LeadDetailsInformationForm
            data={lead}
            editMode={false}
            innerRef={null}
            infoItems={infoItems}
            fetchLeadDetails={fetchLeadDetails}
            updateLeadNameInBreadcrumb={updateLeadNameInBreadcrumb}
          />

          {!addressEdit && (
            <Paper elevation={0} className="lead-details__section">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  className="lead-details__section-title"
                >
                  Address Information
                </Typography>
                <div className="lead-details-section-edit-section">
                  <CreateOutlined
                    onClick={() => {
                      if (
                        lead?.leadStatus !== "Closed-Won" &&
                        lead?.leadStatus !== "Closed-Lost"
                      ) {
                        setAddressEdit(true);
                      }
                    }}
                    className={`lead-details-section-edit-icon ${lead?.leadStatus === "Closed-Won" ||
                      lead?.leadStatus === "Closed-Lost"
                      ? "disabled"
                      : ""
                      }`}
                    fontSize="small"
                    style={{
                      cursor:
                        lead?.leadStatus === "Closed-Won" ||
                          lead?.leadStatus === "Closed-Lost"
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        lead?.leadStatus === "Closed-Won" ||
                          lead?.leadStatus === "Closed-Lost"
                          ? 0.5
                          : 1,
                      marginRight: "-4px",
                      marginTop: "-13px",
                    }}
                  />
                </div>
              </div>
              <Grid container spacing={3}>
                {addressItems.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Typography
                      variant="body2"
                      className="lead-details__item-label"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="lead-details__item-value"
                    >
                      {item.value || "-"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
          {addressEdit && (
            <LeadAddressDetailsInformationForm
              addressEdit={addressEdit}
              setAddressEdit={setAddressEdit}
              lead={lead}
              fetchLeadDetails={fetchLeadDetails}
            />
          )}

          <Box className="lead-details__actions">
            <Button
              variant="contained"
              startIcon={<img src={SendIcon} alt="send" />}
              className="lead-details__btn lead-details__btn-send"
              onClick={() => setSendQuotationOpen(true)}
              disabled={!plannerId}
            >
              Send Quotation
            </Button>
            <Button
              variant="contained"
              startIcon={<img src={CalendarIcon} alt="calendar" />}
              className="lead-details__btn lead-details__btn-book"
              onClick={() => setShowBookPackage(true)}
            >
              Order Package
            </Button>
          </Box>

          {plannerId != null && (
            <SendQuotationModal
              open={sendQuotationOpen}
              onClose={() => setSendQuotationOpen(false)}
              leadId={leadId}
              leadName={lead.name}
              leadEmail={lead.email}
              onSuccess={() => {
                console.log("Quotation sent successfully");
              }}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default LeadDetails;
